import {
    CredentialResponse,
    GoogleLogin,
    useGoogleLogin,
} from "@react-oauth/google";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import userApi from "@/api/user";
import { Google } from "@/components/Icons/Icons";
import { AppText } from "@/components/Text/AppText";
import { User } from "@/types";
import { Tokens } from "@/types/token";
import { Attribution, ReferralSource } from "@/types/user";

export type SSOLogin = {
    setError: (error: string) => void;
    onSuccess: (user: User, tokens: Tokens) => void;
};

export const SSOLogin = ({ setError, onSuccess }: SSOLogin) => {
    const [searchParams] = useSearchParams();
    const referralSource = searchParams.get("utm_source") as ReferralSource;
    const signUpReferralCode = searchParams.get("referral_code");

    const attribution: Attribution = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            utmSource: params.get("utm_source") || undefined,
            utmMedium: params.get("utm_medium") || undefined,
            utmCampaign: params.get("utm_campaign") || undefined,
            utmContent: params.get("utm_content") || undefined,
            utmTerm: params.get("utm_term") || undefined,
            referrer: document.referrer || undefined,
        };
    }, []);
    const onOneTapSuccess = async (credentialResponse: CredentialResponse) => {
        try {
            const { tokens, user } = await userApi.ssoLoginApi({
                type: "credential",
                value: credentialResponse.credential ?? "",
                method: "google",
                referralSource,
                signUpReferralCode: signUpReferralCode ?? undefined,
                attribution,
            });
            onSuccess(user, tokens);
        } catch (error) {
            setError(
                error instanceof Error
                    ? error.message
                    : "Failed to login with Google. Please try again.",
            );
        }
    };

    const onOneTapError = () => {
        setError("Failed to login with Google. Please try again.");
    };

    const loginWithGoogle = useGoogleLogin({
        flow: "auth-code",
        onSuccess: async ({ code }) => {
            try {
                const { tokens, user } = await userApi.ssoLoginApi({
                    type: "code",
                    value: code,
                    method: "google",
                    referralSource,
                    signUpReferralCode: signUpReferralCode ?? undefined,
                    attribution,
                });
                onSuccess(user, tokens);
            } catch (error) {
                setError(
                    error instanceof Error
                        ? error.message
                        : "Failed to login with Google. Please try again.",
                );
            }
        },
        onError: e => {
            if (e?.error !== "access_denied") {
                setError("Failed to login with Google");
            }
        },
    });

    return (
        <div>
            <div>
                <button
                    onClick={() => {
                        loginWithGoogle();
                    }}
                    className="mx-auto flex h-10 items-center justify-center rounded-lg bg-neutral-50 pl-[2px] pr-3 ring-2 ring-[#35475a33] hover:bg-[#F2F2F2]"
                >
                    <Google className="h-10 w-10" />
                    <AppText variant="labelsbuttons">
                        Continue with Google
                    </AppText>
                </button>
            </div>
            <div className="hidden">
                <GoogleLogin
                    useOneTap
                    onSuccess={onOneTapSuccess}
                    onError={onOneTapError}
                    cancel_on_tap_outside={false}
                />
            </div>
        </div>
    );
};
